import { FullNameSchema, PasswordSchema } from '@fundline/shared/schemas/members';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Alert } from '../../components/ui/Alert.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { TextField } from '../../components/ui/Field.tsx';
import { Card, PageHeader, Spinner } from '../../components/ui/Layout.tsx';
import { ThemeToggle } from '../../components/ui/ThemeToggle.tsx';
import { useAuth } from '../../lib/auth.tsx';
import { queryKeys } from '../../lib/queryKeys.ts';
import { supabase } from '../../lib/supabase.ts';
import { useProfileQuery } from './use-profile.ts';

export function AccountPage() {
  const { claims, signOut } = useAuth();
  if (!claims) return null;
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Account"
        description={
          claims.platformOwner
            ? 'Fundline platform owner (read-only access to every workspace).'
            : `Signed in as ${claims.role === 'admin' ? 'an admin' : 'a rep'}.`
        }
      />
      <NameCard userId={claims.userId} />
      <Card title="Appearance">
        <p className="callout mb-3 text-ink-muted">
          System follows your phone or computer. Your choice is saved to your account.
        </p>
        <ThemeToggle />
      </Card>
      <PasswordCard />
      <Card title="Session">
        <p className="callout text-ink-muted">{claims.email}</p>
        {/* Read from the access token itself, which is what the database checks. */}
        <dl
          className="my-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 footnote"
          data-testid="token-claims"
        >
          <dt className="text-ink-muted">workspace_id</dt>
          <dd className="break-all font-mono">{claims.workspaceId ?? 'none'}</dd>
          <dt className="text-ink-muted">ws_role</dt>
          <dd className="font-mono">{claims.role ?? 'none'}</dd>
          <dt className="text-ink-muted">platform_owner</dt>
          <dd className="font-mono">{String(claims.platformOwner)}</dd>
        </dl>
        <Button variant="secondary" onClick={() => void signOut()}>
          Sign out of this device
        </Button>
      </Card>
    </div>
  );
}

function NameCard({ userId }: { userId: string }) {
  const profile = useProfileQuery(userId);
  const queryClient = useQueryClient();
  const [name, setName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: async (fullName: string) => {
      const { error: updateError } = await supabase()
        .from('profiles')
        .update({ full_name: fullName })
        .eq('id', userId);
      if (updateError) throw new Error(`Couldn't save: ${updateError.message}`);
    },
    onSuccess: () => {
      setName(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.profile(userId) });
    },
  });

  if (profile.isPending) return <Spinner />;
  if (profile.isError) return <Alert tone="error">{profile.error.message}</Alert>;

  const value = name ?? profile.data.full_name;
  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const parsed = FullNameSchema.safeParse(value);
    setError(parsed.success ? null : (parsed.error.issues[0]?.message ?? 'Enter a name'));
    if (parsed.success) save.mutate(parsed.data);
  }

  return (
    <Card title="Your name">
      <form className="flex flex-col gap-3" onSubmit={onSubmit} noValidate>
        <TextField
          label="Name"
          autoComplete="name"
          value={value}
          onChange={(e) => setName(e.target.value)}
          error={error}
        />
        {save.isError ? <Alert tone="error">{save.error.message}</Alert> : null}
        {save.isSuccess && name === null ? <Alert tone="success">Saved.</Alert> : null}
        <div>
          <Button type="submit" busy={save.isPending}>
            Save name
          </Button>
        </div>
      </form>
    </Card>
  );
}

function PasswordCard() {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const change = useMutation({
    mutationFn: async (next: string) => {
      const { error: updateError } = await supabase().auth.updateUser({ password: next });
      if (updateError) throw new Error(`Couldn't change the password: ${updateError.message}`);
    },
    onSuccess: () => {
      setPassword('');
      setConfirm('');
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const parsed = PasswordSchema.safeParse(password);
    if (!parsed.success) setError(parsed.error.issues[0]?.message ?? 'Choose a password');
    else if (password !== confirm) setError('The passwords do not match');
    else {
      setError(null);
      change.mutate(parsed.data);
    }
  }

  return (
    <Card title="Password">
      <form className="flex flex-col gap-3" onSubmit={onSubmit} noValidate>
        <TextField
          label="New password"
          type="password"
          autoComplete="new-password"
          hint="At least 10 characters."
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={error}
        />
        <TextField
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
        {change.isError ? <Alert tone="error">{change.error.message}</Alert> : null}
        {change.isSuccess ? <Alert tone="success">Password changed.</Alert> : null}
        <div>
          <Button type="submit" variant="secondary" busy={change.isPending}>
            Change password
          </Button>
        </div>
      </form>
    </Card>
  );
}
